import { SquareDashedKanban } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/overlays";
import { Input, Textarea } from "@/components/ui/primitives";
import { keys } from "@/hooks/queries";
import { api, queryClient, type NodeOut } from "@/lib/api";
import { NODE_COLOR } from "@/lib/palette";
import { useGraph } from "@/stores/graph";

/** Create an organizational hub — a green square in the atlas backed by a
 * markdown file (Organization/<name>.md) in the vault. Its tags/metadata
 * attract newly-ingested documents automatically; existing documents only
 * move when the user explicitly asks to reorganize. */
export function OrgNodeDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [tags, setTags] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setName("");
      setTags("");
      setBody("");
    }
  }, [open]);

  const create = async () => {
    setBusy(true);
    try {
      const node = await api.request<NodeOut>("POST", "/api/nodes", {
        type: "org",
        label: name.trim(),
        keywords: tags
          .split(",")
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean),
        text: body.trim() || null,
      });
      await queryClient.invalidateQueries({ queryKey: keys.overview });
      useGraph.getState().markFlash([node.id]);
      useGraph.getState().requestFocus(node.id);
      useGraph.getState().select({ kind: "node", id: node.id });
      toast.success(`“${node.label}” created`, {
        description: "New documents matching its tags will gather around it.",
      });
      onOpenChange(false);
    } catch (e) {
      toast.error("Could not create the node", { description: String(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="New organizational node" className="w-[min(560px,92vw)]">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) void create();
          }}
        >
          <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3">
            <SquareDashedKanban className="mt-0.5 h-4 w-4 shrink-0" style={{ color: NODE_COLOR.org }} />
            <p className="text-xs leading-relaxed text-muted-foreground">
              An organizational node is a green square that gathers related material — backed by
              a markdown file in your vault. New documents matching its tags attach
              automatically; existing ones only when you choose “Reorganize” on the node.
            </p>
          </div>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name — e.g. homework week five"
            aria-label="Organizational node name"
          />
          <Input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="Tags, comma-separated — e.g. homework, week 5, problem set"
            aria-label="Tags"
          />
          <Textarea
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Notes / metadata (markdown — becomes the file's body)…"
          />
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" disabled={!name.trim() || busy}>
              {busy ? "Creating…" : "Create node"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
